import React, {useEffect, useState, useMemo} from "react";
import { Link } from "react-router-dom";
import {Container, Row, Col, Card, Button, Alert, Spinner} from "react-bootstrap";
import Select from "react-select";
import IncomeExpenseDailyChart from "./reportsmodules/incomeexpensechart";
import { fetchAccounts, fetchStores, fetchUserDetails, apiInstance } from "../../api/api"

const AccountingHomepage = () => {
    const [dashboardAccounts, setDashboardAccounts] = useState([]);
    const [assetAccounts, setAssetAccounts] = useState([]);
    const [totalCurrentAssets, setTotalCurrentAssets] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [stores, setStores] = useState([]);
    const [selectedScope, setSelectedScope] = useState("all");
    const [activeFY, setActiveFY] = useState(null);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    useEffect(() => {
        apiInstance.get('/financial-years/active/')
            .then(res => setActiveFY(res.data))
            .catch(() => setActiveFY(null));
    }, []);

    useEffect(() => {
        // fetch user role once
        fetchUserDetails()
            .then(user => {
                const canFilterStores = user.is_superuser === true || user.is_staff === true;
                setIsAdmin(canFilterStores);
                if (canFilterStores) {
                    fetchStores()
                        .then((data) => setStores(data?.results || data || []))
                        .catch(() => setStores([]));
                }
            })
            .catch(() => setIsAdmin(false));
    }, []);

    useEffect(() => {
        const loadDashboardAccounts = async () => {
            setAccountsLoading(true);
            setAccountsError(null);
            try {
                const filters = selectedScope && selectedScope !== "all"
                    ? { store_id: selectedScope }
                    : {};
                const data = await fetchAccounts(filters);
                const accounts = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.results)
                        ? data.results
                        : null;

                if (accounts === null) {
                    throw new Error("Accounts API returned an invalid response.");
                }

                setDashboardAccounts(data?.results || data || []);
            } catch (error) {
                console.error("Error loading dashboard accounts", error);
                setAccountsError("Failed to load account balances.");
                setDashboardAccounts([]);
            } finally {
                setAccountsLoading(false);
            }
        };

        loadDashboardAccounts();
    }, [selectedScope]);

    useEffect(() => {
        if (dashboardAccounts.length > 0) {
            // filter accounts by name or type
            const filteredAccounts = dashboardAccounts.filter(account =>
                ["Cash", "Kenya Commercial Bank (KCB)", "Equity Bank", "George Mpesa", "Mpesa Till Account",
                    "NCBA Bank"].includes(account.account_name)
            );
            setAssetAccounts(filteredAccounts);

            const total = filteredAccounts.reduce(
                (sum, account) => sum + Number(account.computed_balance || 0),
                0
            );
            setTotalCurrentAssets(total);
        } else {
            setAssetAccounts([]);
            setTotalCurrentAssets(0);
        }
    }, [dashboardAccounts]);

    const scopeOptions = useMemo(() => [
        { value: "all", label: "Overall Company" },
        ...stores.map((store) => ({ value: store.id, label: store.name })),
    ], [stores]);

    const selectedScopeOption = scopeOptions.find(
        (option) => String(option.value) === String(selectedScope)
    ) || scopeOptions[0];

    const scopeLabel = selectedScopeOption?.label || "Overall Company";


    return (
        <Container className="mt-5">
            {/*financial year status*/}
            {activeFY && (
            <Card className="mb-3 bg-light">
                <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Active Financial Year:</strong> {activeFY.name}
                        <br />
                        <small>
                            {activeFY.start_date} - {activeFY.end_date}
                        </small>
                    </div>

                    {isAdmin && (
                        <Link to="/accounting/financial-years">
                            <Button variant="outline-danger" size="sm">
                                Manage Financial Years
                            </Button>
                        </Link>
                    )}
                </Card.Body>
            </Card>
            )}

            <h1 className="text-center mb-4">Accounting Dashboard</h1>
            {isAdmin && (
                <Row className="align-items-end mb-3">
                    <Col md={5}>
                        <label className="form-label">Dashboard Scope</label>
                        <Select
                            options={scopeOptions}
                            value={selectedScopeOption}
                            onChange={(option) => setSelectedScope(option?.value || "all")}
                            isSearchable
                            isClearable={false}
                        />
                    </Col>
                </Row>
            )}

            {accountsError && <Alert variant="danger">{accountsError}</Alert>}

            <Row>
                {/*income expense bar graph*/}
                <Col md={9} className="mb-4">
                    <IncomeExpenseDailyChart storeId={selectedScope} scopeLabel={scopeLabel}/>
                </Col>
                {/*asset balances card*/}
                <Col md={3} className="mb-4">
                    <Card className="text-white bg-info">
                        <Card.Body>
                            <Card.Title>{scopeLabel} Current Asset Balances</Card.Title>
                            {accountsLoading ? (
                                <Spinner animation="border" size="sm" />
                            ) : assetAccounts.map(( account, index) => (
                                <Card.Text key={index}>
                                    {account.account_name}: Ksh {parseFloat(account.computed_balance).toLocaleString()}
                                </Card.Text>
                            ))}
                            {/*<Card.Title>Current Asset Balances</Card.Title>*/}
                            <Card.Text className="font-weight-bold">
                                Total current assets: Ksh {totalCurrentAssets.toLocaleString()}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/accounts" className="text-decoration-none">
                        <Card className="text-white bg-primary">
                            <Card.Body>
                                <Card.Title>Accounts</Card.Title>
                                <Card.Text>View and manage accounts</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/transactions" className="text-decoration-none">
                        <Card className="text-white bg-success">
                            <Card.Body>
                                <Card.Title>Transactions</Card.Title>
                                <Card.Text>View and record transactions</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/reports" className="text-decoration-none">
                        <Card className="text-white bg-warning">
                            <Card.Body>
                                <Card.Title>Reports</Card.Title>
                                <Card.Text>View accounting reports</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
            </Row>
            <Row>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/budgets" className="text-decoration-none">
                        <Card className="text-white bg-info">
                            <Card.Body>
                                <Card.Title>Budgets</Card.Title>
                                <Card.Text>Manage budgets</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/payroll" className="text-decoration-none">
                        <Card className="text-white bg-danger">
                            <Card.Body>
                                <Card.Title>Payroll</Card.Title>
                                <Card.Text>Manage and view payroll</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/taxes" className="text-decoration-none">
                        <Card className="text-white bg-secondary">
                            <Card.Body>
                                <Card.Title>Taxes</Card.Title>
                                <Card.Text>Manage and view taxes</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
            </Row>
            <Row>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/accountsauditlog" className="text-decoration-none">
                        <Card className="text-white bg-dark">
                            <Card.Body>
                                <Card.Title>Audit Log</Card.Title>
                                <Card.Text>View audit log</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
            </Row>
        </Container>
    );
};

export default AccountingHomepage;